import React, { useEffect, useRef } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import {
  checkAppRedirects,
  isBypassPath,
  isOnboardingPath,
} from "utils/routes";
import { useSelector } from "react-redux";
import {
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  AFFILIATION_PATH,
  BASE_BOT_FORM_PATH,
  BASE_BOTS_BACKTESTING_PATH,
  BASE_BOTS_PATH,
  BASE_PUBLIC_BOT_PATH,
  BASE_PUBLIC_EDIT_BOT_PATH,
  CREATE_TRADE_PATH,
  DASHBOARD_PATH,
  DCA_PUBLIC_BOTS_PATH,
  GROUP_MANAGEMENT_PATH,
  MARKETPLACE_PATH,
  ONBOARDING_PATHS,
  PAGE_SPINNER_MARGIN_FROM_TOP,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_ASSETS_PATH,
  SETTINGS_PATH,
  TRADES_PATH,
  USER_SIGNALS_PATH,
  WATCHLIST_PATH,
  MY_GROUPS_PATH,
  ADMIN_TRADING_CONFIGURATION_PATH,
  ADMIN_SIGNALS_PATH,
  PORTFOLIO_PATH,
  IS_TELEGRAM_WEB_APP,
  QUICK_ONBOARDING_PATH,
} from "common/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useConsumeSearchParams } from "utils/hooks/useSearchParams";
import {
  useBaseQueries,
  useHotjar,
  useBottomNavigator,
  useIsOnboarding,
  useScrollToTop,
  useSentryUser,
} from "utils/hooks/misc";
import { AuthUser, TelegramWebappQueryData } from "@shared/types";
import { didUserSkipOnboarding } from "features/onboarding/common/utils";
import { sharedLib } from "@shared/services/shared.service";
import {
  selectWorkspace,
  selectWorkspaceInitialized,
} from "@shared/slices/common.slice";
import { useGetUser } from "@shared/utils/hooks/misc";
import { useInitAdminGroupsSelect } from "features/admin/hooks";
import useWorkspace from "utils/hooks/useWorkspace";
import useLogout from "utils/hooks/useLogout.ts";
import useQuery from "utils/hooks/useQuery.ts";
import ClientsDataProvider from "@shared/features/common/ClientsDataProvider.tsx";
import SymbolsMetadataProvider from "@shared/features/common/SymbolsMetadataProvider.tsx";
import ExchangesMetadataProvider from "@shared/features/common/ExchangesMetadataProvider.tsx";
import AccountFormProvider from "@shared/features/common/AccountFormProvider.tsx";
import PageWrapper from "common/Home/PageWrapper.tsx";
import PrivateRoute from "common/Routes/PrivateRoute.tsx";
import CreateAccountDrawer from "features/portfolio/CreateAccount/CreateAccountDrawer.tsx";
import { useTheme } from "styled-components";
import { lazyLoad } from "utils/misc.tsx";
import QueriesError from "common/Empty/QueriesError.tsx";
import * as Sentry from "@sentry/react";
import Drawers from "common/Home/Drawers.tsx";

const Accounts = lazyLoad(() => import("features/portfolio/Accounts/Accounts"));
const Watchlist = lazyLoad(() => import("features/watchlist/Watchlist"));
const TradingTerminalWrapper = lazyLoad(
  () => import("features/tradingTerminal/TradingTerminalWrapper")
);
const Assets = lazyLoad(() => import("features/portfolio/Assets/Assets"));
const GroupManagement = lazyLoad(
  () => import("features/admin/group/GroupManagement")
);
const Settings = lazyLoad(() => import("features/settings/Settings"));
const Dashboard = lazyLoad(() => import("features/dashboard/Dashboard"));
const SignalsPage = lazyLoad(() => import("features/signals/SignalsPage"));
const BotForm = lazyLoad(() => import("features/bots/BotForm/BotForm"));
const Bots = lazyLoad(() => import("features/bots/Bots"));
const PublicBotPreview = lazyLoad(
  () => import("features/bots/PublicBots/PublicBotPreview")
);
const TradesPage = lazyLoad(() => import("features/trades/TradesPage"));
const RedirectWithQuery = lazyLoad(
  () => import("common/Routes/RedirectWithQuery")
);
const Affiliation = lazyLoad(() => import("features/affiliation/Affiliation"));
const OnboardingProvider = lazyLoad(
  () => import("features/onboarding/OnboardingProvider")
);
const TradingConfiguration = lazyLoad(
  () => import("features/admin/TradingConfiguration/TradingConfiguration")
);
const BacktestingPage = lazyLoad(
  () =>
    import("features/bots/BotForm/Backtesting/BacktestingPage/BacktestingPage")
);
const Marketplace = lazyLoad(
  () => import("features/newMarketplace/Marketplace")
);
const PublicBots = lazyLoad(
  () => import("features/bots/PublicBots/PublicBots")
);
const MyGroupsPage = lazyLoad(
  () => import("features/bots/Groups/MyGroupsPage")
);
const LeaveReviewModal = lazyLoad(
  () => import("features/misc/LeaveReviewModal")
);
const PortfolioPage = lazyLoad(
  () => import("features/portfolio/PortfolioPage")
);
const AcceptTermsModal = lazyLoad(
  () => import("features/misc/AcceptTermsModal")
);
const TelegramNotificationsModal = lazyLoad(
  () => import("features/misc/TelegramNotificationsModal")
);
const QuickOnboarding = lazyLoad(
  () => import("features/quickOnboarding/QuickOnboarding")
);
const BotsPage = lazyLoad(() => import("features/bots/BotsPage"));

interface HomeProps {
  telegramStartValue?: TelegramWebappQueryData;
}

const Home = ({ telegramStartValue }: HomeProps) => {
  const theme = useTheme();
  const workspace = useSelector(selectWorkspace);
  const workspaceInitialized = useSelector(selectWorkspaceInitialized);
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const [logout, { isLoading: isLoadingLogout }] = useLogout();
  const status = useGetUser();
  const { isSuccess, isError } = status;
  const isOnboarding = useIsOnboarding();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  sharedLib.sharedApi.common.useGetAvailableIconAssetsQuery(
    !token || isOnboarding ? skipToken : undefined
  );
  useScrollToTop();

  const getUserError = (isSuccess && !user) || isError;
  const shouldLogout = getUserError && !isLoadingLogout;
  useEffect(() => {
    if (shouldLogout) {
      logout().catch(() => {});
    }
  }, [shouldLogout]);

  return (
    <LoadingSpinnerOverlay
      isLoading={!user || !workspaceInitialized}
      spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
      spinnerSize={"large"}
      hideContentWhileLoading={true}
      backgroundColor={theme.colors.layoutBgColor}
    >
      {user && workspaceInitialized && (
        <ClientsDataProvider>
          <SymbolsMetadataProvider>
            <ExchangesMetadataProvider>
              <AccountFormProvider>
                <PageWrapper user={user}>
                  <Content
                    user={user}
                    key={workspace}
                    telegramStartValue={telegramStartValue}
                  />
                  {!isOnboarding && <CreateAccountDrawer />}
                </PageWrapper>
              </AccountFormProvider>
            </ExchangesMetadataProvider>
          </SymbolsMetadataProvider>
        </ClientsDataProvider>
      )}
    </LoadingSpinnerOverlay>
  );
};

export default Home;

interface ContentProps {
  user: AuthUser;
  telegramStartValue?: TelegramWebappQueryData;
}

const Content = ({ user, telegramStartValue }: ContentProps) => {
  const query = useQuery();
  const theme = useTheme();
  const location = useLocation();
  useConsumeSearchParams();
  const [finishOnboarding] =
    sharedLib.sharedApi.authentication.useFinishOnboardingMutation();
  const isOnboarding = useIsOnboarding();
  const skipOnboarding = didUserSkipOnboarding(user.id);
  useBaseQueries();
  useHotjar();
  useSentryUser();
  useInitAdminGroupsSelect();
  const { getHomePath, getValidWorkspacesByPath } = useWorkspace();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isRedirectedRef = useRef(false);
  const workspace = useSelector(selectWorkspace);

  const redirect = checkAppRedirects(
    user,
    location,
    query,
    getValidWorkspacesByPath,
    getHomePath,
    telegramStartValue,
    isRedirectedRef.current,
    workspace
  );

  useEffect(() => {
    if (!isRedirectedRef.current) {
      isRedirectedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (
      user.is_onboarding &&
      !isOnboardingPath(location) &&
      !isBypassPath(location) &&
      !redirect
    ) {
      finishOnboarding();
    }
  }, [isOnboarding, skipOnboarding]);

  if (redirect) return redirect;

  const getPortfolioItems = () => {
    if (isBottomNavigator) {
      return <PrivateRoute path={PORTFOLIO_PATH} component={PortfolioPage} />;
    } else {
      return [
        <PrivateRoute
          key="assets"
          path={PORTFOLIO_ASSETS_PATH}
          component={Assets}
        />,
        <PrivateRoute
          key="accounts"
          path={PORTFOLIO_ACCOUNTS_PATH}
          component={Accounts}
        />,
      ];
    }
  };

  const getBotsItems = () => {
    if (isBottomNavigator) {
      return (
        <PrivateRoute
          path={[
            BASE_BOTS_BACKTESTING_PATH,
            DCA_PUBLIC_BOTS_PATH,
            BASE_BOTS_PATH,
          ]}
          component={BotsPage}
        />
      );
    } else {
      return [
        <PrivateRoute
          key="backtesting"
          path={BASE_BOTS_BACKTESTING_PATH}
          component={BacktestingPage}
        />,
        <PrivateRoute
          key="publicBots"
          path={DCA_PUBLIC_BOTS_PATH}
          component={PublicBots}
        />,
        <PrivateRoute key="myBots" path={BASE_BOTS_PATH} component={Bots} />,
      ];
    }
  };

  return (
    <Sentry.ErrorBoundary fallback={<QueriesError isGeneralPageError={true} />}>
      <React.Suspense
        fallback={
          <LoadingSpinnerOverlay
            isLoading={true}
            spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
            spinnerSize={"large"}
            wrap={true}
            hideContentWhileLoading={true}
            backgroundColor={theme.colors.layoutBgColor}
          />
        }
      >
        <Switch>
          {isBottomNavigator && (
            <PrivateRoute
              path={QUICK_ONBOARDING_PATH}
              component={QuickOnboarding}
            />
          )}
          <PrivateRoute
            path={ONBOARDING_PATHS}
            component={OnboardingProvider}
          />
          <PrivateRoute
            path={GROUP_MANAGEMENT_PATH}
            component={GroupManagement}
          />
          <PrivateRoute path={DASHBOARD_PATH} component={Dashboard} />
          <PrivateRoute path={WATCHLIST_PATH} component={Watchlist} />
          <PrivateRoute path={MARKETPLACE_PATH} component={Marketplace} />
          <PrivateRoute
            path={[
              BASE_PUBLIC_EDIT_BOT_PATH,
              BASE_BOT_FORM_PATH,
              ADMIN_TRADING_CONFIGURATION_FORM_PATH,
            ]}
            component={BotForm}
          />
          <PrivateRoute
            path={ADMIN_TRADING_CONFIGURATION_PATH}
            component={TradingConfiguration}
          />
          <PrivateRoute
            path={BASE_PUBLIC_BOT_PATH}
            component={PublicBotPreview}
          />
          {getBotsItems()}
          <PrivateRoute path={MY_GROUPS_PATH} component={MyGroupsPage} />
          {getPortfolioItems()}
          <PrivateRoute
            path={CREATE_TRADE_PATH}
            component={TradingTerminalWrapper}
          />
          <PrivateRoute path={TRADES_PATH} component={TradesPage} />
          <PrivateRoute path={SETTINGS_PATH} component={Settings} />
          <PrivateRoute
            path={[USER_SIGNALS_PATH, ADMIN_SIGNALS_PATH]}
            component={SignalsPage}
          />
          <PrivateRoute path={AFFILIATION_PATH} component={Affiliation} />
          <Route exact path="/">
            <RedirectWithQuery to={getHomePath()} />
          </Route>
        </Switch>
        <LeaveReviewModal />
        <AcceptTermsModal />
        <Drawers />
        {IS_TELEGRAM_WEB_APP && <TelegramNotificationsModal />}
      </React.Suspense>
    </Sentry.ErrorBoundary>
  );
};
